<script>
import {useI18n} from "vue-i18n";
import Icon from "../../components/Icon.vue";
import DonorReportExporter from "./DonorReportExporter.vue";
import AccordionItem from "../../components/accordion/AccordionItem.vue";
import {useReportStore} from "../../../stores/report";
import {useAnalyticEventStore} from "../../../stores/analyticEvents";
import {useOrganizationStore} from "../../../stores/organizations";
import DonationReportExporter from "../donations/DonationReportExporter.vue";
import ReportHistory from "./ReportHistory.vue";
import HouseholdReportExporter from "./HouseholdReportExporter.vue";
import GivingTrendReportExporter from "./GivingTrendReport.vue";

export default {
  name: "ReportsPanel",
  components: {
    HouseholdReportExporter, ReportHistory, DonationReportExporter, AccordionItem, DonorReportExporter, Icon, GivingTrendReportExporter},
  setup() {
    // I18n
    const {t} = useI18n();
    const reportStore = useReportStore();
    const analyticEventStore = useAnalyticEventStore();
    const organizationStore = useOrganizationStore();

    return {
      t,
      reportStore,
      analyticEventStore,
      organizationStore
    };
  },
  data() {
    return {
      isVisible: false,
      selectedCategory: null,
      selectedReport: null,
      reportSections: [
        {
          id: 'donors',
          icon: 'users',
          reports: [
            {id: 'donor_list'},
            {id: 'year_end_summary'},
            {id: 'contribution_statement'},
            {id: 'giving_trends'}
          ]
        },
        {
          id: 'households',
          icon: 'house-solid-full',
          reports: [
            {id: 'household_giving_summary'},
            {id: 'household_member_list'}
          ]
        },
        {
          id: 'donations',
          icon: 'hand-heart',
          reports: [
            {id: 'donation_report'},
          ]
        },
        {
          id: 'history',
          title: 'History',
          icon: 'clock-rotate-left',
        },

        // For another tab
        // {
        //   id: 'other',
        //   title: 'Other',
        //   icon: 'folder',
        //   reports: []
        // },

      ]
    };
  },
  computed: {
    showReportGenerator() {
      return this.selectedReport && this.selectedCategory !== 'history';
    },

  },
  watch: {},
  mounted() {
    setTimeout(() => {
      this.isVisible = true;
    }, 100);

    // Pre-fetch donors so they're cached before a report is selected
    this.reportStore.getUnscopedDonors();
  },
  methods: {
    selectCategory(categoryId) {
      this.selectedCategory = categoryId;
      this.analyticEventStore.setOrganizationId(this.organizationStore.organizationId);
      this.analyticEventStore.logEvent({ action: `report-category-selected-${categoryId}` });
      if(categoryId === 'history') {
        this.selectedReport = null;
      }
    },

    selectReport(reportId) {
      this.selectedReport = reportId;
      this.analyticEventStore.setOrganizationId(this.organizationStore.organizationId);
      this.analyticEventStore.logEvent({ action: `report-selected-${reportId}` });
    },

    isActiveCategory(categoryId) {
      return this.selectedCategory === categoryId;
    },

    isActiveReport(reportId) {
      return this.selectedReport === reportId;
    },

    showHistory() {
      return this.selectedCategory === 'history';
    },

  },
};
</script>

<template>
  <div class="reports-container">
    <!-- Main Sidebar - Categories -->
    <div
      class="main-sidebar"
      :class="{ 'sidebar-visible': isVisible }"
    >
      <nav class="category-nav">
        <div
          v-for="(section, index) in reportSections"
          :key="section.id"
          class="sidebar-item"
          :class="{ 'selected': isActiveCategory(section.id) }"
          :style="{ 'animation-delay': `${index * 100}ms` }"
          @click="selectCategory(section.id)"
        >
          <accordion-item
            click-to-toggle
            class="w-full"
            :no-chevron="section.id === 'history'"
            start-open
          >
            <template #titleSlot>
              <div class="flex flex-row icon-hover-container">
                <icon
                  class="mr-2"
                  :icon="section.icon || 'chart-pie'"
                  animate-hover
                />
                <div class="label">
                  {{ t(`reports['${section.id}']["header"]`) }}
                </div>
              </div>
            </template>
            <template #bodySlot>
              <div
                v-for="(report) in section.reports"
                :key="report.id"
                class="sidebar-item secondary-item"
                :class="{ 'selected': isActiveReport(report.id) }"
                @click="selectReport(report.id)"
              >
                <span class="label">
                  {{ t(`reports['${section.id}']['${report.id}']["title"]`) }}
                </span>
              </div>
            </template>
          </accordion-item>
        </div>
      </nav>
    </div>

    <!-- Main Content Area -->
    <div
      class="main-content"
      :class="{ 'content-visible': isVisible }"
    >
      <!-- Report Generator -->

      <div
        v-if="showReportGenerator"
        class="content-section"
      >
        <donor-report-exporter
          v-if="selectedReport === 'donor_list'"
          report-type="donor_list"
          :title="t('reports.donors.donor_list.title')"
          :description="t('reports.donors.donor_list.description')"
        />

        <donor-report-exporter
          v-if="selectedReport === 'year_end_summary'"
          show-years
          report-type="year_end_summary"
          :title="t('reports.donors.year_end_summary.title')"
          :description="t('reports.donors.year_end_summary.description')"
        />

        <donor-report-exporter
          v-if="selectedReport === 'contribution_statement'"
          show-years
          report-type="contribution_statement"
          :title="t('reports.donors.contribution_statement.title')"
          :description="t('reports.donors.contribution_statement.description')"
        />

        <donation-report-exporter
          v-if="selectedReport === 'donation_report'"
          :title="t('reports.donations.donation_report.title')"
          :description="t('reports.donations.donation_report.description')"
        />

        <giving-trend-report-exporter
          v-if="selectedReport === 'giving_trends'"
          :title="t('reports.donors.giving_trends.title')"
          :description="t('reports.donors.giving_trends.description')"
        />

        <household-report-exporter
          v-if="selectedReport === 'household_giving_summary' || selectedReport === 'household_member_list'"
          :show-years="selectedReport === 'household_giving_summary'"
          :report-type="selectedReport"
          :title="t(`reports.households.${selectedReport}.title`)"
          :description="t(`reports.households.${selectedReport}.description`)"
        />
      </div>

      <!-- History View -->
      <div
        v-else-if="showHistory()"
        key="history"
        class="content-section"
      >
        <report-history />
      </div>

      <!-- Default State -->
      <div
        v-else
        key="default"
        class="content-section"
      >
        <div class="welcome-content">
          <h2 class="welcome-title">
            {{ t('reports.welcome') || 'Welcome to Reports' }}
          </h2>
          <p class="welcome-text">
            {{ t('reports.selectCategory') || 'Select a category from the sidebar to get started' }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">

.reports-container {
  @apply h-full flex bg-gray-50 p-0 m-0;

  max-height: 93dvh;
}

// Main Sidebar
.main-sidebar {
  @apply border-r border-x-2 border-gray-200;
}


.sidebar-item {
  &:nth-child(1) {
    @apply mt-4;
  }

  @apply flex flex-row gap-2 rounded-md mx-2;
  @apply cursor-pointer py-2 items-center;

  .secondary-item {
    @apply pl-4 m-0 mt-1;

    &:hover, &.selected {
      @apply bg-default_secondary text-white;
    }
  }

  .label {
    @apply font-semibold;
  }
}

.sidebar-item {

  :deep(.accordion-item) {
    box-shadow: none;
  }

  :deep(.accordion-header-container) {
    @apply bg-gray-100 text-default_secondary_dark;
    @apply m-0 px-4;

    .icon svg path {
      @apply fill-default_secondary_dark;
    }
  }
}


.main-sidebar {
  @apply w-52 py-2 bg-white overflow-y-auto;
}

.reports-nav {
  @apply space-y-3;
}

.empty-reports {
  @apply text-center py-12;
}

.empty-icon {
  @apply w-12 h-12 mx-auto mb-4 text-gray-400;
}

.empty-text {
  @apply text-gray-500;
}

// Main Content
.main-content {
  @apply flex-1 opacity-0 transform translate-y-4 transition-all duration-700 ease-out;
  @apply max-h-full h-full;
}

.content-visible {
  @apply opacity-100 transform translate-y-0;
}

.content-section {
  @apply bg-white border border-gray-200 h-full max-h-full;
}

.content-title {
  @apply text-2xl font-bold text-gray-800 p-6 border-b border-gray-200;
}

.generator-content,
.history-content {
  @apply p-6;
}

.generator-placeholder,
.history-placeholder {
  @apply bg-gray-50 rounded-lg p-12 min-h-96 flex flex-col items-center justify-center;
}

.placeholder-icon {
  @apply w-20 h-20 mb-6 text-gray-400;
}

.placeholder-text {
  @apply text-xl font-semibold text-gray-700 mb-4;
}

.placeholder-details {
  @apply text-center;
}

.details-text {
  @apply text-gray-500;
}

.welcome-content {
  @apply flex flex-col items-center justify-center h-full py-20;
}

.welcome-icon {
  @apply w-24 h-24 mb-6 text-default_primary;
}

.welcome-title {
  @apply text-3xl font-bold text-gray-800 mb-4;
}

.welcome-text {
  @apply text-lg text-gray-600 text-center max-w-md;
}

// Animations
.slide-in-right-enter-active {
  @apply transition-all duration-300 ease-out;
}

.slide-in-right-leave-active {
  @apply transition-all duration-200 ease-in;
}

.slide-in-right-enter-from {
  @apply opacity-0 transform translate-x-full;
}

.slide-in-right-leave-to {
  @apply opacity-0 transform translate-x-full;
}

.fade-in-enter-active {
  @apply transition-all duration-300 ease-out;
}

.fade-in-leave-active {
  @apply transition-all duration-200 ease-in;
}

.fade-in-enter-from {
  @apply opacity-0 transform translate-y-4;
}

.fade-in-leave-to {
  @apply opacity-0 transform translate-y-4;
}

@keyframes fade-in-left {
  from {
    opacity: 0;
    transform: translateX(1rem);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.animate-fade-in-left {
  animation: fade-in-left 0.6s ease-out forwards;
}

// Staggered animation delays
.nav-button:nth-child(1) {
  animation-delay: 0ms;
}

.nav-button:nth-child(2) {
  animation-delay: 100ms;
}

.nav-button:nth-child(3) {
  animation-delay: 200ms;
}

.nav-button:nth-child(4) {
  animation-delay: 300ms;
}


// Responsive design
@media (max-width: 1024px) {
  .main-sidebar {
    @apply w-56;
  }

}

@media (max-width: 768px) {
  .reports-container {
    @apply flex-col;
  }

  .main-sidebar {
    @apply w-full;
  }

  .main-content {
    @apply min-h-screen;
  }
}

</style>
